/**
 * Service de cache IndexedDB pour les données d'entreprise et d'institution
 * 
 * STRATÉGIE: Offline-First / Stale-While-Revalidate
 * 
 * 1. Lecture: IndexedDB d'abord (instantané) → UI affiche les données
 * 2. Background: Vérifier avec le backend si des changements existent
 * 3. Si changements: Mettre à jour IndexedDB + notifier l'UI
 * 4. Écriture: Sauvegarder en local + sync avec backend
 * 
 * @module cacheService
 */

// ============================================
// TYPES ET INTERFACES
// ============================================

export interface CacheMetadata {
  /** ID unique de l'enregistrement */
  id: string;
  /** Type de données (company, institution, subscription, user) */
  type: CacheDataType;
  /** Timestamp de dernière modification locale */
  localUpdatedAt: number;
  /** Timestamp de dernière sync avec le backend */
  lastSyncedAt: number;
  /** Version des données du backend (pour détecter les conflits) */
  version: number;
  /** Hash des données pour détecter les changements */
  hash: string;
  /** Statut de synchronisation */
  syncStatus: 'synced' | 'pending' | 'conflict' | 'error';
  /** ID utilisateur propriétaire */
  userId: string;
}

export interface CachedData<T = unknown> {
  /** Données mises en cache */
  data: T;
  /** Métadonnées de cache */
  metadata: CacheMetadata;
}

export type CacheDataType = 'company' | 'institution' | 'subscription' | 'user' | 'plans';

export interface SyncResult {
  success: boolean;
  hasChanges: boolean;
  conflictData?: unknown;
  error?: string;
}

// ============================================
// CONFIGURATION INDEXEDDB
// ============================================

const DB_NAME = 'wanzo_cache';
const DB_VERSION = 1;

const STORES = {
  COMPANY: 'company',
  INSTITUTION: 'institution',
  SUBSCRIPTION: 'subscription',
  USER: 'user',
  PLANS: 'plans',
  METADATA: 'metadata'
} as const;

// Durée de validité du cache avant revalidation (en ms)
const CACHE_TTL = {
  company: 5 * 60 * 1000,      // 5 minutes
  institution: 5 * 60 * 1000,   // 5 minutes
  subscription: 10 * 60 * 1000, // 10 minutes
  user: 2 * 60 * 1000,          // 2 minutes
  plans: 60 * 60 * 1000         // 1 heure (les plans changent rarement)
};

// ============================================
// CLASSE PRINCIPALE
// ============================================

class CacheService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  // ============================================
  // INITIALISATION
  // ============================================

  /**
   * Initialise la connexion IndexedDB
   */
  async init(): Promise<IDBDatabase> {
    // Si déjà connecté, retourner la connexion existante
    if (this.db) {
      return this.db;
    }

    // Si initialisation en cours, attendre
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Erreur ouverture IndexedDB:', request.error);
        this.initPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialisé avec succès');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('🔄 Mise à jour du schéma IndexedDB...');

        // Créer les stores si ils n'existent pas
        if (!db.objectStoreNames.contains(STORES.COMPANY)) {
          db.createObjectStore(STORES.COMPANY, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.INSTITUTION)) {
          db.createObjectStore(STORES.INSTITUTION, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.SUBSCRIPTION)) {
          db.createObjectStore(STORES.SUBSCRIPTION, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.USER)) {
          db.createObjectStore(STORES.USER, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.PLANS)) {
          db.createObjectStore(STORES.PLANS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          const metaStore = db.createObjectStore(STORES.METADATA, { keyPath: 'id' });
          metaStore.createIndex('type', 'type', { unique: false });
          metaStore.createIndex('userId', 'userId', { unique: false });
          metaStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Vérifie si IndexedDB est disponible
   */
  isAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  // ============================================
  // OPÉRATIONS CRUD GÉNÉRIQUES
  // ============================================

  /**
   * Récupère des données du cache
   */
  async get<T>(type: CacheDataType, id: string): Promise<CachedData<T> | null> {
    if (!this.isAvailable()) {
      console.warn('⚠️ IndexedDB non disponible');
      return null;
    }

    try {
      const db = await this.init();
      const storeName = this.getStoreName(type);

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName, STORES.METADATA], 'readonly');
        const store = transaction.objectStore(storeName);
        const metaStore = transaction.objectStore(STORES.METADATA);

        const dataRequest = store.get(id);
        const metaRequest = metaStore.get(`${type}_${id}`);

        transaction.oncomplete = () => {
          const data = dataRequest.result;
          const metadata = metaRequest.result;

          if (data && metadata) {
            resolve({ data, metadata });
          } else if (data) {
            // Données sans métadonnées (migration)
            resolve({
              data,
              metadata: this.createDefaultMetadata(id, type, '')
            });
          } else {
            resolve(null);
          }
        };

        transaction.onerror = () => {
          console.error('❌ Erreur lecture cache:', transaction.error);
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error('❌ Erreur get cache:', error);
      return null;
    }
  }

  /**
   * Sauvegarde des données dans le cache
   */
  async set<T>(
    type: CacheDataType,
    id: string,
    data: T,
    userId: string,
    syncStatus: CacheMetadata['syncStatus'] = 'synced'
  ): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      const db = await this.init();
      const storeName = this.getStoreName(type);
      const hash = this.hashData(data);

      const metadata: CacheMetadata = {
        id: `${type}_${id}`,
        type,
        localUpdatedAt: Date.now(),
        lastSyncedAt: syncStatus === 'synced' ? Date.now() : 0,
        version: 1,
        hash,
        syncStatus,
        userId
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName, STORES.METADATA], 'readwrite');
        const store = transaction.objectStore(storeName);
        const metaStore = transaction.objectStore(STORES.METADATA);

        // Ajouter l'ID aux données si pas présent
        const dataWithId = { ...(data as object), id };

        store.put(dataWithId);
        metaStore.put(metadata);

        transaction.oncomplete = () => {
          console.log(`✅ Cache ${type} mis à jour:`, id);
          this.notifyListeners(type, id, dataWithId);
          resolve();
        };

        transaction.onerror = () => {
          console.error('❌ Erreur écriture cache:', transaction.error);
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error('❌ Erreur set cache:', error);
    }
  }

  /**
   * Supprime des données du cache
   */
  async delete(type: CacheDataType, id: string): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      const db = await this.init();
      const storeName = this.getStoreName(type);

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName, STORES.METADATA], 'readwrite');
        const store = transaction.objectStore(storeName);
        const metaStore = transaction.objectStore(STORES.METADATA);

        store.delete(id);
        metaStore.delete(`${type}_${id}`);

        transaction.oncomplete = () => {
          console.log(`🗑️ Cache ${type} supprimé:`, id);
          resolve();
        };

        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Erreur delete cache:', error);
    }
  }

  /**
   * Vide tout le cache d'un type
   */
  async clearType(type: CacheDataType): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      const db = await this.init();
      const storeName = this.getStoreName(type);

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        store.clear();

        transaction.oncomplete = () => {
          console.log(`🧹 Cache ${type} vidé`);
          resolve();
        };

        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Erreur clearType:', error);
    }
  }

  /**
   * Vide tout le cache
   */
  async clearAll(): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const storeNames = Array.from(db.objectStoreNames);
        const transaction = db.transaction(storeNames, 'readwrite');

        storeNames.forEach(storeName => {
          transaction.objectStore(storeName).clear();
        });

        transaction.oncomplete = () => {
          console.log('🧹 Tout le cache vidé');
          resolve();
        };

        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Erreur clearAll:', error);
    }
  }

  // ============================================
  // VALIDATION ET SYNCHRONISATION
  // ============================================

  /**
   * Vérifie si le cache est encore valide (pas expiré)
   */
  isCacheValid(metadata: CacheMetadata): boolean {
    const ttl = CACHE_TTL[metadata.type] || CACHE_TTL.company;
    const age = Date.now() - metadata.lastSyncedAt;
    return age < ttl;
  }

  /**
   * Vérifie si les données ont changé (comparaison de hash)
   */
  hasDataChanged<T>(cachedData: CachedData<T>, newData: T): boolean {
    const newHash = this.hashData(newData);
    return cachedData.metadata.hash !== newHash;
  }

  /**
   * Met à jour le statut de synchronisation
   */
  async updateSyncStatus(
    type: CacheDataType,
    id: string,
    syncStatus: CacheMetadata['syncStatus'],
    version?: number
  ): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORES.METADATA], 'readwrite');
        const metaStore = transaction.objectStore(STORES.METADATA);
        const metaId = `${type}_${id}`;

        const request = metaStore.get(metaId);
        
        request.onsuccess = () => {
          const metadata = request.result as CacheMetadata;
          if (metadata) {
            metadata.syncStatus = syncStatus;
            metadata.lastSyncedAt = Date.now();
            if (version !== undefined) {
              metadata.version = version;
            }
            metaStore.put(metadata);
          }
        };

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Erreur updateSyncStatus:', error);
    }
  }

  // ============================================
  // OBSERVATEURS (LISTENERS)
  // ============================================

  /**
   * S'abonner aux changements d'un type de données
   */
  subscribe<T = unknown>(type: CacheDataType, id: string, callback: (data: T) => void): () => void {
    const key = `${type}_${id}`;
    
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    
    // Les listeners d'une même clé reçoivent les données du type qu'ils ont demandé
    const listener = callback as (data: unknown) => void;
    this.listeners.get(key)!.add(listener);
    
    // Retourner une fonction de désabonnement
    return () => {
      this.listeners.get(key)?.delete(listener);
    };
  }

  /**
   * Notifier les listeners d'un changement
   */
  private notifyListeners(type: CacheDataType, id: string, data: unknown): void {
    const key = `${type}_${id}`;
    const callbacks = this.listeners.get(key);
    
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Erreur dans listener cache:', error);
        }
      });
    }
  }

  // ============================================
  // UTILITAIRES
  // ============================================

  /**
   * Obtient le nom du store pour un type de données
   */
  private getStoreName(type: CacheDataType): string {
    switch (type) {
      case 'company': return STORES.COMPANY;
      case 'institution': return STORES.INSTITUTION;
      case 'subscription': return STORES.SUBSCRIPTION;
      case 'user': return STORES.USER;
      case 'plans': return STORES.PLANS;
      default: return STORES.COMPANY;
    }
  }

  /**
   * Crée des métadonnées par défaut
   */
  private createDefaultMetadata(
    id: string,
    type: CacheDataType,
    userId: string
  ): CacheMetadata {
    return {
      id: `${type}_${id}`,
      type,
      localUpdatedAt: Date.now(),
      lastSyncedAt: 0,
      version: 0,
      hash: '',
      syncStatus: 'pending',
      userId
    };
  }

  /**
   * Calcule un hash simple des données pour détecter les changements
   */
  private hashData(data: unknown): string {
    try {
      // Object.keys lève sur null/undefined : l'erreur est rattrapée plus bas, comme avant
      const str = JSON.stringify(data, Object.keys(data as object).sort());
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString(36);
    } catch {
      return Date.now().toString(36);
    }
  }

  /**
   * Obtient des statistiques sur le cache
   */
  async getStats(): Promise<{
    totalItems: number;
    pendingSync: number;
    byType: Record<CacheDataType, number>;
  }> {
    if (!this.isAvailable()) {
      return { totalItems: 0, pendingSync: 0, byType: {} as Record<CacheDataType, number> };
    }

    try {
      const db = await this.init();
      const stats = {
        totalItems: 0,
        pendingSync: 0,
        byType: {} as Record<CacheDataType, number>
      };

      const types: CacheDataType[] = ['company', 'institution', 'subscription', 'user', 'plans'];

      for (const type of types) {
        const storeName = this.getStoreName(type);
        const count = await new Promise<number>((resolve) => {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const request = store.count();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(0);
        });
        stats.byType[type] = count;
        stats.totalItems += count;
      }

      // Compter les pending
      const pendingCount = await new Promise<number>((resolve) => {
        const transaction = db.transaction([STORES.METADATA], 'readonly');
        const store = transaction.objectStore(STORES.METADATA);
        const index = store.index('syncStatus');
        const request = index.count(IDBKeyRange.only('pending'));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });
      stats.pendingSync = pendingCount;

      return stats;
    } catch (error) {
      console.error('❌ Erreur getStats:', error);
      return { totalItems: 0, pendingSync: 0, byType: {} as Record<CacheDataType, number> };
    }
  }
}

// Export singleton
export const cacheService = new CacheService();
export default cacheService;
